import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { RanchPortalLinks } from '@/components/RanchPortalLinks';
import { ClubCard } from '@/components/ClubCard';
import { YogaSessionCard } from '@/components/YogaSessionCard';
import { TAKE_A_BREAK_BOOK_CLUB } from '@/lib/communityClubs';
import { YOGA_SESSIONS } from '@/lib/yogaSessions';
import { useRanchEvents } from '@/hooks/useRanchEvents';
import { useNotices } from '@/hooks/useNotices';
import {
  eventSourceLabel,
  formatEventTimeRange,
  groupEventsByDate,
  isThisWeek,
  type RanchEvent,
} from '@shared/ranch-feed';

/**
 * What's On: town resident-portal calendars plus the seeded Babcock
 * National HOA Member Central snapshot. Lives on Home + this page — the
 * bottom Calendar tab is Flights, not these events.
 *
 * This is the one screen that answers "what is on this weekend," which the
 * media kit could only describe vaguely ("nearly every Friday evening").
 * The portal feeds give real dates and times, so Food Truck Friday shows up
 * as Friday 5:00-9:00 PM; HOA club/lifestyle/dining/golf events sit beside
 * them as `kind: hoa_national`.
 */

const FILTERS = [
  { key: 'week', label: 'This week' },
  { key: 'all', label: 'Upcoming' },
  { key: 'community_events', label: 'Community' },
  { key: 'parks_events', label: 'Parks & Rec' },
  { key: 'hoa_national', label: 'National' },
] as const;

type FilterKey = (typeof FILTERS)[number]['key'];

function initialFilter(search: string): FilterKey {
  const v = new URLSearchParams(search).get('filter');
  if (
    v === 'week' ||
    v === 'all' ||
    v === 'community_events' ||
    v === 'parks_events' ||
    v === 'hoa_national'
  ) {
    return v;
  }
  return 'week';
}

/**
 * Friendly date heading. Compares ISO date strings against the server's
 * notion of "today" in town time, so this stays correct regardless of the
 * device's own timezone.
 */
function dateHeading(iso: string, today: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  // Construct at noon UTC to stay clear of DST boundaries.
  const dt = new Date(Date.UTC(y, m - 1, d, 12));
  const weekday = dt.toLocaleDateString('en-US', { weekday: 'long', timeZone: 'UTC' });
  const monthDay = dt.toLocaleDateString('en-US', { month: 'long', day: 'numeric', timeZone: 'UTC' });

  if (iso === today) return `Today · ${weekday}, ${monthDay}`;

  const [ty, tm, td] = today.split('-').map(Number);
  const tomorrow = new Date(Date.UTC(ty, tm - 1, td, 12));
  tomorrow.setUTCDate(tomorrow.getUTCDate() + 1);
  if (iso === tomorrow.toISOString().slice(0, 10)) return `Tomorrow · ${weekday}, ${monthDay}`;

  return `${weekday}, ${monthDay}`;
}

export function EventsPage() {
  const { search, hash } = useLocation();
  const navigate = useNavigate();
  const initial = useMemo(() => initialFilter(search), [search]);
  const [filter, setFilter] = useState<FilterKey>(initial);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const { data, error, loading } = useRanchEvents(45);
  const notices = useNotices();
  const todayNotices = useMemo(() => {
    const alerts = notices.filter((n) => n.severity === 'alert');
    const rest = notices.filter((n) => n.severity !== 'alert');
    return [...alerts, ...rest];
  }, [notices]);

  useEffect(() => {
    setFilter(initial);
  }, [initial]);

  useEffect(() => {
    const q = new URLSearchParams();
    if (filter !== 'all') q.set('filter', filter);
    const nextSearch = q.toString();
    if (nextSearch !== search.replace(/^\?/, '')) {
      navigate({ pathname: '/events', search: nextSearch, hash }, { replace: true });
    }
  }, [filter, hash, navigate, search]);

  useEffect(() => {
    const id = hash.replace(/^#/, '');
    if (!id.startsWith('event-') || !data) return;
    const eventId = id.slice('event-'.length);
    setExpanded((prev) => {
      if (prev.has(eventId)) return prev;
      const next = new Set(prev);
      next.add(eventId);
      return next;
    });
    const el = document.getElementById(id);
    el?.scrollIntoView({ block: 'start' });
  }, [data, hash]);

  const groups = useMemo(() => {
    if (!data) return [];
    const filtered = data.events.filter((e) => {
      if (filter === 'week') return isThisWeek(e.date, data.today);
      if (filter === 'community_events' || filter === 'parks_events' || filter === 'hoa_national') {
        return e.kind === filter;
      }
      return true;
    });
    return groupEventsByDate(filtered);
  }, [data, filter]);

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">What&apos;s On</h1>
        <p className="page-subtitle">
          Community and Parks &amp; Rec town calendar, plus Babcock National Member Central
        </p>
      </div>

      <div className="card">
        <p style={{ fontSize: 14, color: 'var(--color-ink-muted)', lineHeight: 1.5 }}>
          Register for classes and rent lodges, courts, or fields on the town&apos;s Rec1 catalog —
          this app does not take bookings.
        </p>
        <RanchPortalLinks />
      </div>

      {todayNotices.length > 0 && (
        <div className="card">
          <h2>Today at Babcock</h2>
          {todayNotices.map((n) => (
            <div key={n.id} className="note-block">
              <div className="note-label">{n.title}</div>
              {n.body}
            </div>
          ))}
        </div>
      )}

      <ClubCard club={TAKE_A_BREAK_BOOK_CLUB} />

      <div className="section-title" style={{ marginTop: 18 }}>
        Yoga &amp; gentle movement
      </div>
      <p style={{ fontSize: 13, color: 'var(--color-ink-muted)', marginBottom: 4, lineHeight: 1.5 }}>
        September 2026 Parks &amp; Rec calendar. Yin, Yoga in the Park, Tai Chi, and Sound Bath
        are the soft options. Power Yoga is a Baptiste flow — not gentle.
      </p>
      {YOGA_SESSIONS.map((session) => (
        <YogaSessionCard key={session.id} session={session} />
      ))}

      {/* Wrapping chip row — never horizontal-scroll. */}
      <div className="chip-row" role="tablist" aria-label="Event type">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            role="tab"
            aria-selected={filter === f.key}
            className={`chip ${filter === f.key ? 'chip--active' : ''}`}
            onClick={() => setFilter(f.key)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {error && (
        <div className="card">
          <p style={{ color: 'var(--color-ink-muted)' }}>
            Events are temporarily unavailable. Use the town calendar or Rec1 links above.
          </p>
        </div>
      )}

      {loading && !data && (
        <div className="card">
          <div className="skeleton" style={{ height: 90 }} />
        </div>
      )}

      {(data?.stale || data?.ranchUnavailable) && (
        <div className="card" style={{ borderLeft: '4px solid var(--color-sun)' }}>
          <p style={{ fontSize: 13, color: 'var(--color-ink-muted)' }}>
            {data.ranchUnavailable
              ? 'Town calendar is unavailable just now — Babcock National events below are from the in-repo snapshot.'
              : 'Showing the last successful update — the resident portal did not respond just now.'}
          </p>
        </div>
      )}

      {data && groups.length === 0 && (
        <div className="empty-state">
          <p>
            {filter === 'week'
              ? 'No events listed this week.'
              : 'No events listed in the next several weeks.'}
          </p>
        </div>
      )}

      {groups.map((g) => (
        <div key={g.date} style={{ marginTop: 14 }}>
          <div className="section-title">{dateHeading(g.date, data!.today)}</div>
          {g.events.map((e) => {
            const isOpen = expanded.has(e.id);
            return (
              <EventCard key={e.id} event={e} isOpen={isOpen} onToggle={() => toggle(e.id)} />
            );
          })}
        </div>
      ))}

      {data && (
        <p style={{ fontSize: 12, color: 'var(--color-ink-muted)', marginTop: 18, lineHeight: 1.5 }}>
          Sources:{' '}
          <a href={data.source.url} target="_blank" rel="noreferrer">
            {data.source.name}
          </a>
          {data.source.hoaNational && (
            <>
              {' · '}
              <a href={data.source.hoaNational.calendarUrl} target="_blank" rel="noreferrer">
                {data.source.hoaNational.name}
              </a>{' '}
              (seeded snapshot)
            </>
          )}
          . Times and details are set by the town and the HOA, not by this app — confirm before
          making plans.
        </p>
      )}
    </div>
  );
}

function EventCard({
  event: e,
  isOpen,
  onToggle,
}: {
  event: RanchEvent;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="card" id={`event-${e.id}`} style={{ marginTop: 8 }}>
      {/* Time sits ABOVE the title rather than beside it. Inline,
          a long title like "Hip Hop & Jazz Dance Class" wraps to a
          second line that starts at the title box's left edge -
          indented past the time - which reads as a stray centered
          word. Stacking gives every title the full card width. */}
      <div
        style={{
          fontSize: 13,
          fontWeight: 700,
          color: 'var(--color-primary)',
          letterSpacing: '0.02em',
        }}
      >
        {formatEventTimeRange(e)}
      </div>
      <h2 style={{ fontSize: 16, margin: '2px 0 0' }}>{e.title}</h2>
      <p style={{ fontSize: 12, color: 'var(--color-ink-muted)', marginTop: 2 }}>
        {eventSourceLabel(e.kind)}
      </p>

      {e.location && (
        <p style={{ fontSize: 13, color: 'var(--color-ink-muted)', marginTop: 4 }}>{e.location}</p>
      )}

      {e.description && (
        <>
          <p
            style={{
              fontSize: 14,
              marginTop: 8,
              lineHeight: 1.5,
              display: '-webkit-box',
              WebkitLineClamp: isOpen ? 'unset' : 3,
              WebkitBoxOrient: 'vertical',
              overflow: isOpen ? 'visible' : 'hidden',
            }}
          >
            {e.description}
          </p>
          <button
            type="button"
            onClick={onToggle}
            style={{
              marginTop: 6,
              background: 'none',
              border: 'none',
              padding: 0,
              font: 'inherit',
              fontSize: 13,
              fontWeight: 700,
              color: 'var(--color-primary)',
              cursor: 'pointer',
            }}
          >
            {isOpen ? 'Show less' : 'Show more'}
          </button>
        </>
      )}

      {e.url && (
        <div className="action-row" style={{ marginTop: 10 }}>
          <a className="action-btn" href={e.url} target="_blank" rel="noreferrer">
            Details
          </a>
        </div>
      )}
    </div>
  );
}
